import React from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Alert, Layout, Button, Row, Col, InputNumber, message } from 'antd';
import { connect } from 'react-redux';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { withRouter } from 'react-router';
import {
	nullSafeGetOrElse,
	nullSafeGet,
	getCurrency,
	isSubContractor,
	isInternalTeam,
} from '../../utils/DataAccessUtils';
import { debounce } from '../../utils/PerformanceUtils';
import {
	createProposalSubContractor,
	proposalsRestCrudThunksForBuyer,
	proposalsRestCrudThunksForSupplier,
	updateProposalSubContractor,
} from '../../thunks/proposals_thunks';
import FormDots from '../common/FormDots';
import {
	workOrdersRestCrudThunksForBuyer,
	workOrdersRestCrudThunksForSupplier,
} from '../../thunks/work_orders_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { isBuyerOrInternalTech, isInternalTech } from '../../utils/AuthUtils';
import OWRichTextEditor from '../../rich_text_editor/OWRichTextEditor';
import ClearErrors from '../clear_errors/ClearErrors';
import { PROPOSALS_CRUD_ACTION_CREATORS } from '../../actions/proposals_actions';
import LaborFormFields from '../buyer_invoice_form/LaborFormFields';
import { TIME_IN } from '../supplier_invoice_form/LaborTimeSelect';
import {
	getQuoteInvoiceLineItems,
	initialiseFormDataWithLineItems,
	initialiseQuoteInvoiceCommonTotals,
	initializeLineItemKeys,
	LINE_ITEM_FIELD,
} from '../../utils/InvoiceUtils';
import PartFormFields from '../buyer_invoice_form/PartFormFields';
import EquipmentFormFields from '../buyer_invoice_form/EquipmentFormFields';
import BackButton from '../back_button/BackButton';
import FileUploader from '../file_uploader/FileUploader';
import { getContractorWO } from '../../utils/WorkOrderUtils';
import {
	quotesRestCrudThunksForBuyer,
	quotesRestCrudThunksForSupplier,
} from '../../thunks/quotes_thunks';
import SimpleLineItemFormFields from '../buyer_invoice_form/SimpleLineItemFormFields';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { currentUser } from '../../mock_data/mocks';
import {
	supplierFacilitiesRestCrudThunksForBuyer,
	supplierFacilitiesRestCrudThunksForSupplier,
} from '../../thunks/supplier_facilities_thunks';

require('./ProposalForm.less');

let partUuid = 1;
let laborUuid = 1;
let miscUuid = 1;
let travelUuid = 1;
let freightUuid = 1;
let incurredUuid = 1;
let equipmentUuid = 1;
interface ProposalFormProps extends FormComponentProps {
	formData: any;
	onSuccess: any;
	creating: boolean;
	updating: boolean;
	createErrors: string[];
	updateErrors: string[];
	redirectForwardUrl: string;
	redirectBackwardUrl: string;
	currentUser: any;
	history: any;
	createProposal: any;
	createContractorProposal: any;
	updateProposal: any;
	updateContractorProposal: any;
	getWorkOrder: any;
	workOrder: any;
	userType: any;
	isClientView: boolean;
	copyMode: boolean;
}

class ProposalForm extends React.Component<ProposalFormProps, any> {
	state = {
		formDataInitialized: false,
		laborItemInitialValues: {},
		partItemInitialValues: {},
		equipmentItemInitialValues: {},
		miscItemInitialValues: {},
		travelItemInitialValues: {},
		freightItemInitialValues: {},
		incurredItemInitialValues: {},
		timeIn: TIME_IN.HOURS,
		editorRef: React.createRef(),
		formData: {},
		supplierFacility: {},
	};

	componentDidMount() {
		const { getWorkOrder, formData, userType, currentUser, fetchSupplierFacility } = this.props;

		getWorkOrder(formData.workOrderId).then((workOrder) => {
			if (userType === ROLE_TYPES.BUYER || isInternalTech(currentUser)) {
				if (formData.supplierFacilityId) {
					fetchSupplierFacility(formData.supplierFacilityId).then((supplierFacility) => {
						this.setState({ supplierFacility });
					});
				} else {
					this.setState({ supplierFacility: nullSafeGet('supplierFacility', workOrder) });
				}
			} else {
				this.setState({ supplierFacility: nullSafeGet('facility', currentUser) });
			}
		});

		this.recalculateTotals();

		const {
			labor,
			part,
			equipment,
			misc,
			travel,
			freight,
			incurred,
			formData: newFormData,
		} = initialiseFormDataWithLineItems(formData, TIME_IN.HOURS);
		laborUuid = labor;
		partUuid = part;
		equipmentUuid = equipment;
		miscUuid = misc;
		travelUuid = travel;
		freightUuid = freight;
		incurredUuid = incurred;

		this.setState({ formDataInitialized: true, formData: { ...formData, ...newFormData } });
	}

	getScope = () =>
		nullSafeGet('state.editorRef.current', this) &&
		(this.state.editorRef.current as any).getContent();

	handleFileAttachmentUploadChange = (newAttachments) => {
		const { form } = this.props;
		form.setFieldsValue({ attachments: newAttachments });
	};

	handleSubmit = (e) => {
		const {
			form,
			formData,
			redirectForwardUrl,
			currentUser,
			history,
			onSuccess,
			createProposal,
			updateProposal,
			userType,
			workOrder,
			createContractorProposal,
			updateContractorProposal,
			isClientView,
			copyMode,
		} = this.props;
		const contractorWO = getContractorWO(workOrder, copyMode || isClientView);
		e.preventDefault();

		form.validateFields((err, values) => {
			if (!err) {
				const submitAction = values.id ? updateProposal : createProposal;
				const submitContractorAction = values.id
					? updateContractorProposal
					: createContractorProposal;

				const {
					laborItems,
					miscItems,
					freightItems,
					travelItems,
					incurredItems,
					materialLineItems,
				} = getQuoteInvoiceLineItems(values, this.state.timeIn);

				const taxRate =
					nullSafeGetOrElse('totalBeforeTax', values, 0) > 0
						? (nullSafeGetOrElse('tax', values, 0) * 100.0) / nullSafeGet('totalBeforeTax', values)
						: 0.0;

				const proposal = {
					...formData,
					id: values.id,
					scope: this.getScope(),
					attachments: nullSafeGetOrElse('attachments', values, []),
					status: isBuyerOrInternalTech(currentUser) ? 'pending' : 'draft',
					taxRate: `${taxRate}`,
					tax: `${nullSafeGetOrElse('tax', values, 0)}`,
					totalBeforeTax: `${nullSafeGetOrElse('totalBeforeTax', values, 0)}`,
					totalAfterTax: `${nullSafeGetOrElse('totalAfterTax', values, 0)}`,
					laborTotalBeforeTax: `${nullSafeGetOrElse('laborTotalBeforeTax', values, 0)}`,
					laborLineItems: laborItems || [],
					materialTotalBeforeTax: `${nullSafeGetOrElse('materialTotalBeforeTax', values, 0)}`,
					materialLineItems: materialLineItems || [],
					freightTotalBeforeTax: `${nullSafeGetOrElse('freightTotalBeforeTax', values, 0)}`,
					freightLineItems: freightItems || [],
					travelTotalBeforeTax: `${nullSafeGetOrElse('travelTotalBeforeTax', values, 0)}`,
					travelLineItems: travelItems || [],
					miscTotalBeforeTax: `${parseFloat(nullSafeGetOrElse('miscTotalBeforeTax', values, 0))}`,
					miscLineItems: miscItems || [],
					incurredCostTotalBeforeTax: `${nullSafeGetOrElse(
						'incurredCostTotalBeforeTax',
						values,
						0
					)}`,
					incurredCostLineItems: incurredItems || [],
					workOrderId: parseInt(formData.workOrderId, 10),
					requestForProposalId: formData.requestForProposalId
						? parseInt(formData.requestForProposalId, 10)
						: undefined,
					supplierFacilityId: nullSafeGet('supplierFacilityId', values),
					declineNotes: formData.declineNotes,
					isBuyerUploaded: userType === ROLE_TYPES.BUYER || isInternalTech(currentUser),
				};
				if (
					userType === ROLE_TYPES.SUPPLIER &&
					contractorWO &&
					isSubContractor(currentUser, workOrder)
				) {
					submitContractorAction({
						...proposal,
						workOrderId: undefined,
						supplierFacilityId: undefined,
						contractorWorkOrderId: contractorWO.id,
						contractorSupplierFacilityId: contractorWO.contractorSupplierFacilityId,
						subSupplierFacilityId: contractorWO.subSupplierFacilityId,
					})
						.then((record) => {
							if (redirectForwardUrl) {
								history.push(redirectForwardUrl);
							}
							if (onSuccess) {
								onSuccess(record);
							}
						})
						.catch((err) => {
							console.log('err', err);
							if (err && err.startsWith('Proposal already exists for this Work order')) {
								message.error(err);
								history.goBack();
							}
						});
				} else {
					submitAction({
						...proposal,
						workOrderId: parseInt(formData.workOrderId, 10),
						supplierFacilityId: nullSafeGet('supplierFacilityId', values),
						contractorWorkOrderId: undefined,
						contractorSupplierFacilityId: undefined,
						subSupplierFacilityId: undefined,
					})
						.then((record) => {
							if (redirectForwardUrl) {
								history.push(redirectForwardUrl);
							}
							if (onSuccess) {
								onSuccess(record);
							}
						})
						.catch((err) => {
							console.log('err', err);
							if (err && err.startsWith('Proposal already exists for this Work order')) {
								message.error(err);
								history.goBack();
							}
						});
				}
			}
		});
	};

	recalculateTotals = debounce(() => {
		const { form } = this.props;
		const subtotals = form.getFieldsValue([
			'laborTotalBeforeTax',
			'partsTotalBeforeTax',
			'equipmentTotalBeforeTax',
			'freightTotalBeforeTax',
			'travelTotalBeforeTax',
			'miscTotalBeforeTax',
			'incurredCostTotalBeforeTax',
			'tax',
		]);

		const totalBeforeTax = (
			parseFloat(nullSafeGetOrElse('laborTotalBeforeTax', subtotals, 0)) +
			parseFloat(nullSafeGetOrElse('partsTotalBeforeTax', subtotals, 0)) +
			parseFloat(nullSafeGetOrElse('equipmentTotalBeforeTax', subtotals, 0)) +
			parseFloat(nullSafeGetOrElse('freightTotalBeforeTax', subtotals, 0)) +
			parseFloat(nullSafeGetOrElse('travelTotalBeforeTax', subtotals, 0)) +
			parseFloat(nullSafeGetOrElse('miscTotalBeforeTax', subtotals, 0)) +
			parseFloat(nullSafeGetOrElse('incurredCostTotalBeforeTax', subtotals, 0))
		).toFixed(2);

		const totalAfterTax = (
			parseFloat(totalBeforeTax) + parseFloat(nullSafeGetOrElse('tax', subtotals, 0))
		).toFixed(2);

		const materialTotalBeforeTax = (
			parseFloat(nullSafeGetOrElse('partsTotalBeforeTax', subtotals, 0)) +
			parseFloat(nullSafeGetOrElse('equipmentTotalBeforeTax', subtotals, 0))
		).toFixed(2);

		form.setFieldsValue({
			totalBeforeTax,
			totalAfterTax,
			materialTotalBeforeTax,
		});
	}, 200);

	setValuesInState = (val) => this.setState(val);

	render() {
		const {
			creating,
			updating,
			updateErrors,
			createErrors,
			form,
			workOrder,
			currentUser,
			userType,
		} = this.props;
		const submitting = creating || updating;
		const errorMessagesText = createErrors.concat(updateErrors).join('; ');
		const { formData, formDataInitialized, supplierFacility } = this.state;
		if (!formDataInitialized) {
			return <PageLoadingPlaceholder />;
		}
		const isUpdate = formData.hasOwnProperty('id') && formData.id !== undefined;
		const isOnBehalfOfAnotherSupplier =
			userType === ROLE_TYPES.BUYER || isInternalTech(currentUser);
		let submitText = isUpdate ? 'Update proposal' : 'Create proposal';
		let titleText = submitText;
		if (isOnBehalfOfAnotherSupplier && nullSafeGet('displayName', supplierFacility)) {
			titleText = titleText.concat(` on behalf of ${nullSafeGet('displayName', supplierFacility)}`);
		}
		const { getFieldDecorator, getFieldValue } = form;
		const currency = isUpdate
			? getCurrency({ workOrder })
			: getCurrency({ supplier: supplierFacility });

		getFieldDecorator('id', { initialValue: formData.id });
		getFieldDecorator('supplierFacilityId', { initialValue: nullSafeGet('id', supplierFacility) });

		getFieldDecorator(`totalBeforeTax`, {
			initialValue: nullSafeGet(`totalBeforeTax`, formData),
		});

		getFieldDecorator(`totalAfterTax`, {
			initialValue: nullSafeGet(`totalAfterTax`, formData),
		});
		const isThirdParty = () => userType === ROLE_TYPES.SUPPLIER && !isInternalTech(currentUser);

		const taxIgnoredForNTEByBuyer = nullSafeGet(
			'buyerCompany.buyerCompanySettings.config.invoiceConfig.nteCheckWithoutTax',
			workOrder
		);
		initializeLineItemKeys(formData, getFieldDecorator);
		initialiseQuoteInvoiceCommonTotals(formData, getFieldDecorator);

		//TODO: UPGRADE FORM
		return (
			<Layout.Content>
				<h5 style={{ marginBottom: 32 }}>{titleText}</h5>
				<Row className="supplierProposalForm buyerInvoiceForm" gutter={16}>
					<Col span={24}>
						<Form hideRequiredMark={true} layout="vertical">
							<ClearErrors actionCreators={PROPOSALS_CRUD_ACTION_CREATORS} />
							{createErrors.length > 0 || updateErrors.length > 0 ? (
								<Form.Item label="">
									<Alert message={errorMessagesText} type="error" />
								</Form.Item>
							) : null}
							<Row>
								{isUpdate ? (
									formData.status === 'awarded' ? (
										<Alert
											style={{
												flex: 1,
												borderWidth: 0,
												marginBottom: 8,
											}}
											type={'warning'}
											showIcon={true}
											message={
												<span style={{ paddingLeft: '8px' }}>
													If you edit an approved quote, it will have to go back through the
													approval process.
												</span>
											}
										/>
									) : null
								) : isThirdParty ? (
									<Alert
										style={{
											flex: 1,
											borderWidth: 0,
											marginBottom: 8,
											backgroundColor: '#ebfbff',
										}}
										type={'info'}
										showIcon={true}
										message={
											<span style={{ paddingLeft: '8px' }}>
												Want to submit a quote in another currency?{' '}
												<a href={'/supplier/company/me'} target={'_blank'} rel="noreferrer">
													Click here
												</a>{' '}
												to change your company currency.
											</span>
										}
									/>
								) : null}
							</Row>
							<div
								className="supplierProposalForm__sectionHead"
								style={{ marginBottom: 32, flexDirection: 'column', alignItems: 'start' }}
							>
								<div className="supplierProposalForm__sectionHeadPrimary">Scope</div>
								{getFieldDecorator('scope', {
									initialValue: nullSafeGet(`scope`, formData),
								})(
									<OWRichTextEditor
										className="w-full"
										initialEditorState={nullSafeGet(`scope`, formData)}
										editorRef={this.state.editorRef}
									/>
								)}
							</div>
							<Form.Item label="File Attachments" style={{ width: 400 }}>
								{form.getFieldDecorator('attachments', {
									initialValue: formData.attachments || [],
								})(
									<FileUploader
										defaultFileList={formData.attachments || []}
										roleType={userType}
										handleUploadSuccess={this.handleFileAttachmentUploadChange}
										showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
										uploaderType="dragger"
									/>
								)}
							</Form.Item>
							<SimpleLineItemFormFields
								fieldName={LINE_ITEM_FIELD.INCURRED}
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={incurredUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<LaborFormFields
								formData={formData}
								form={form}
								currency={currency}
								initialLaborUuid={laborUuid}
								recalculateTotals={this.recalculateTotals}
								state={this.state}
								setState={this.setValuesInState}
							/>
							<PartFormFields
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={partUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<EquipmentFormFields
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={equipmentUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<SimpleLineItemFormFields
								fieldName={LINE_ITEM_FIELD.TRAVEL}
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={travelUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<SimpleLineItemFormFields
								fieldName={LINE_ITEM_FIELD.FREIGHT}
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={freightUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<SimpleLineItemFormFields
								fieldName={LINE_ITEM_FIELD.MISC}
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={miscUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<div style={{ marginBottom: 32 }}>
								<div className="supplierProposalForm__sectionHead">
									<div className="supplierProposalForm__sectionHeadPrimary">Total Before Tax</div>
									<FormDots />
									<div className="supplierProposalForm__sectionHeadSecondary">
										{currency.format(getFieldValue('totalBeforeTax'))}
									</div>
								</div>
							</div>
							<div style={{ marginBottom: 32 }}>
								{taxIgnoredForNTEByBuyer ? (
									<div className="flex flex-row text-gray-400">
										Buyer doesn't want you to submit taxes for this quote!
									</div>
								) : null}
								<div className="supplierProposalForm__sectionHead">
									<div className="supplierProposalForm__sectionHeadPrimary">Tax</div>
									<FormDots />
									<div className="supplierProposalForm__sectionHeadSecondary">
										{getFieldDecorator(`tax`, {
											validateTrigger: ['onBlur'],
											initialValue: taxIgnoredForNTEByBuyer
												? undefined
												: nullSafeGet(`tax`, formData),
										})(
											<InputNumber
												step={1}
												disabled={taxIgnoredForNTEByBuyer}
												onChange={this.recalculateTotals}
												style={{ width: 240 }}
												addonAfter={currency.id}
											/>
										)}
									</div>
								</div>
							</div>
							<div style={{ marginBottom: 32 }}>
								<div className="supplierProposalForm__sectionHead">
									<div className="supplierProposalForm__sectionHeadPrimary">Total With Tax</div>
									<FormDots />
									<div className="supplierProposalForm__sectionHeadSecondary">
										{currency.format(getFieldValue('totalAfterTax'))}
									</div>
								</div>
							</div>
							<Form.Item label="">
								<span className="mr-4">
									<BackButton buttonText="Cancel" />
								</span>
								<Button
									type={'primary'}
									onClick={(e) => {
										this.handleSubmit(e);
									}}
									loading={submitting}
								>
									{submitText}
								</Button>
							</Form.Item>
						</Form>
					</Col>
				</Row>
			</Layout.Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	formData: ownProps.formData,
	onSuccess: ownProps.onSuccess,
	updating: state.proposals.updating,
	creating: state.proposals.creating,
	createErrors: state.proposals.createErrors,
	updateErrors: state.proposals.updateErrors,
	redirectForwardUrl: ownProps.redirectForwardUrl,
	redirectBackwardUrl: ownProps.redirectBackwardUrl,
	currentUser: state.session.currentUser,
	history: ownProps.history,
	location: ownProps.location,
	workOrder: state.work_orders.detail,
	isClientView: state.clientView.isClientView,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchSupplierFacility: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? supplierFacilitiesRestCrudThunksForSupplier.readOne(id)
				: supplierFacilitiesRestCrudThunksForBuyer.readOne(id)
		),
	getWorkOrder: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrdersRestCrudThunksForSupplier.readOne(id)
				: workOrdersRestCrudThunksForBuyer.readOne(id)
		),
	createProposal: (proposal) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? quotesRestCrudThunksForSupplier.create(proposal)
				: quotesRestCrudThunksForBuyer.create(proposal)
		),
	updateProposal: (proposal) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? quotesRestCrudThunksForSupplier.update(proposal)
				: quotesRestCrudThunksForBuyer.update(proposal)
		),
	createContractorProposal: (proposal) => dispatch(createProposalSubContractor(proposal)),
	updateContractorProposal: (proposal) => dispatch(updateProposalSubContractor(proposal)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(ProposalForm)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(Form.create<ProposalFormProps>()(ComponentWithoutUserType));
